/**
 * Browser implementation of the online client's platform seams (`NetEnv`, core net/env.ts): the page's WebSocket and
 * RTCPeerConnection, plus the dev-only `?relay=1` switch and the Vite build id.
 */
import { browserConfig } from '../browser-platform';
import { clientVersionFor, type NetEnv, type WebSocketLike } from '@hf/client-core/net/env';
import { createOnlineClientWith } from '@hf/client-core/net';
import type { CreateOnlineClient } from '@hf/client-core/net/types';
import { withAccountSockets } from './account';

const LOCAL_HOST = /^(localhost|127\.0\.0\.1|\[::1\]|.+\.localhost|.+\.test)$/;

/**
 * Dev-only `?relay=1`: no WebRTC, so every match goes through the server's WebSocket relay (relay fallback tests,
 * e2e). Honoured in dev builds and on local hosts (a production build served by the local server for e2e), never on
 * a deployed site.
 */
export const relayForced = (
  loc: Pick<Location, 'search' | 'hostname'> | undefined = globalThis.location,
  dev: boolean = (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV === true,
): boolean => {
  if (!loc) return false;
  let on = false;
  try {
    on = new URLSearchParams(loc.search).get('relay') === '1';
  } catch {
    return false;
  }
  return on && (dev || LOCAL_HOST.test(loc.hostname));
};

export const browserNetEnv = (): NetEnv => ({
  createSocket: (url) => new WebSocket(url) as unknown as WebSocketLike,
  RTCPeerConnection: relayForced()
    ? null
    : typeof globalThis.RTCPeerConnection === 'function'
      ? globalThis.RTCPeerConnection
      : null,
  now: () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (id) => clearTimeout(id),
  random: Math.random,
  platform: 'web',
  log: {
    info: (...a) => console.info('[net]', ...a),
    warn: (...a) => console.warn('[net]', ...a),
  },
});

/** Client build id for `hello.clientVersion` (Vite injects VITE_BUILD_ID in CI; dev builds say so). */
export const clientVersion = (): string => clientVersionFor(browserConfig());

/** Default WebSocket URL: same origin `/ws` (Vite proxies it to :8787 in dev). */
export const defaultServerUrl = (): string => {
  const { protocol, host } = window.location;
  return `${protocol === 'https:' ? 'wss' : 'ws'}://${host}/ws`;
};

/**
 * THE online client factory used by src/app: a WebSocket lobby client with reconnect/backoff, clock sync,
 * WebRTC (perfect negotiation, unreliable DataChannel) and automatic relay fallback.
 */
export const createOnlineClient: CreateOnlineClient = (ui) =>
  createOnlineClientWith(ui, withAccountSockets(browserNetEnv()), defaultServerUrl);
